import {
  type CustomFieldDto,
  CustomFieldTypeDto,
  type CustomFieldValueDto,
  type FieldConfigurationDto,
  type IdeaSummaryDto,
  type InputFieldsDto,
} from '@tempo-io/fim3-dto';
import DOMPurify from 'dompurify';

export const getDateDisplay = (date: Date | string, month?: 'long' | 'short') => {
  if (!date) return '';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: month ?? 'short',
    day: 'numeric',
  });
};

// This is a calendar date, not a point in time, so it needs special handling
export const getCustomFieldDateDisplay = (fieldValue: string | Date, month?: 'long' | 'short') => {
  let displayDate: Date | undefined;
  // Custom field date values are stored as strings "YYYY-MM-DD" (see customDatetoIsoDateString)
  if (typeof fieldValue === 'string') {
    const isoCalendarDate = fieldValue as string;
    // Parsing with dayjs or Date will convert to local time, causing it to be off-by-one day if
    // the local time is not GMT, so manually parse and construct a Date
    const dateParts = isoCalendarDate.split('-');
    if (dateParts.length !== 3) return '';
    const numericDateParts = dateParts.map((o) => parseInt(o, 10));
    if (numericDateParts.some((n) => Number.isNaN(n))) return '';
    displayDate = new Date(
      numericDateParts[0] ?? 0,
      (numericDateParts[1] ?? 0) - 1,
      numericDateParts[2],
    );
  } else if (fieldValue !== null && fieldValue !== undefined && fieldValue instanceof Date) {
    // Sometimes the value is converted into a Date object.
    // It only happens when you have navigated to "Custom Fields" before "Feedback Inbox"
    // It looks like some wackiness with react-query I can't figure out
    // (it will be a string on first render, then a Date for subsequent renders),
    // so just workaround it for now by grabbing the UTC date for display
    const date = fieldValue as Date;
    displayDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }
  return displayDate ? getDateDisplay(displayDate, month) : '';
};

export const getDateTimeDisplay = (date: Date | string) => {
  if (!date) return '';
  const dateObj = new Date(date);
  return `${dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  })} at ${dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

export const isValidEmailDomain = (domain: string): boolean => {
  const domainRegex = /^(@?)[a-zA-Z0-9]+([.-][0-9a-zA-Z]+)*\.[a-zA-Z]{2,}$/;
  return domainRegex.test(domain);
};

export const getIdeaFieldsWithMissingCustomFieldsAdded = (
  ideaViewCustomFields: CustomFieldDto[],
  idea: IdeaSummaryDto,
): CustomFieldValueDto[] => {
  if (!ideaViewCustomFields) return [];

  let inSync = true;
  let ideaCustomFields = idea.fields;

  ideaViewCustomFields.forEach((customField) => {
    if (!ideaCustomFields.some((ideaCustomField) => ideaCustomField.fieldId === customField.id)) {
      inSync = false;
      ideaCustomFields = [
        ...ideaCustomFields,
        {
          fieldId: customField.id,
          name: customField.name,
          required: false,
          order: ideaCustomFields.length,
          priorityFrameworkId: customField.priorityFrameworkId,
          saveData: {
            values: [],
            fieldConfiguration: customField.fieldConfiguration,
            isSystem: customField.isSystem,
          },
        },
      ];
    }
  });

  return inSync ? [] : ideaCustomFields;
};

export const removeSystemFieldNamePrefix = (name: string): string => {
  if (!name) return name;
  return name.replace(/^_/, '');
};

export const getFormulaFieldMissingCustomFields = (
  fieldConfigDto: FieldConfigurationDto,
  selectedFields: number[],
): InputFieldsDto => {
  return fieldConfigDto.fieldType === CustomFieldTypeDto.FORMULA
    ? fieldConfigDto.inputFields.filter(
        (inputField) => !selectedFields.includes(inputField.fieldId),
      )
    : [];
};

export const isSystemField = (fieldOrName: CustomFieldDto | string | undefined | null): boolean => {
  if (!fieldOrName) return false;

  if (typeof fieldOrName === 'string') {
    return fieldOrName.startsWith('_');
  }

  return fieldOrName.priorityFrameworkId != null;
};

export const getSafeUrl = (url: string): string | null => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url, window.location.href);

    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return null;
    }

    const sanitized = DOMPurify.sanitize(parsedUrl.href, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      RETURN_TRUSTED_TYPE: false,
      ALLOWED_URI_REGEXP: /^https?:/,
    });

    return sanitized || null;
  } catch {
    return null;
  }
};

export const getSafeName = (name: string): string => {
  let decodedName = name;

  try {
    decodedName = decodeURIComponent(name);
  } catch {
    // If decoding fails (malformed URI), use original name
  }

  return DOMPurify.sanitize(decodedName, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    RETURN_TRUSTED_TYPE: false,
  });
};
